import {
  CheckboxVisibility,
  ConstrainMode,
  DetailsListLayoutMode,
  DetailsRow,
  Dialog,
  DialogType,
  IColumn,
  ICommandBarItemProps,
  IconButton,
  IDetailsRowProps,
  Label,
  ProgressIndicator,
  SelectionMode,
  ShimmeredDetailsList,
  Stack,
  Text,
  TooltipHost,
  FontIcon,
  TextField,
  IDropdownOption,
} from '@fluentui/react';
import { Link } from '@micro-frontend-react/employee-experience/lib/Link';
import * as React from 'react';
import * as PopupText from '../../../Common/CommonModels/PopupText';
import { SubscriberRecord } from '../../../Common/ConsumerModels/SubscriberRecord';
import * as Styled from '../../../Common/Styles/portalPage.styled';
import { PortalPageHeadingSection, PortalPageTitleText } from '../../../Common/Styles/portalPage.styled';
import { useGetAllSubscribers } from '../../../Hooks/Consumer/useGetAllSubscribers';
import { AddSubscriberPanel } from './AddSubscriberPanel';
import { sortItems } from '../../../Common/Components/SortItems';
import { commandBarRefreshItem } from '../../../Common/Components/RefreshButton';
import { SubscriberStatusEnum } from '../../../Common/CommonModels/SubscriberStatusEnum';
import * as StatusStyled from '../../../Common/Styles/portalManagement.styled';

type ConsumerPageProps = {
  addNew: boolean;
};

export function SubscriberPage(props: ConsumerPageProps): React.ReactElement {
  const { addNew } = props;
  const [addSubscriberPanelOpen, setAddSubscriberPanelOpen] = React.useState<boolean>(addNew);

  // Draft Subscriber State
  const [selectedEditSubscriber, setSelectedEditSubscriber] = React.useState<SubscriberRecord>(null);
  const [editSubscriberPanelOpen, setEditSubscriberPanelOpen] = React.useState<boolean>(false);

  const [showConfirmationPopup, setShowConfirmationPopup] = React.useState<boolean>(false);
  const [showSuccessMessage, setShowSuccessMessage] = React.useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = React.useState<boolean>(false);

  const shouldReload = showSuccessMessage && addSubscriberPanelOpen == false;
  const [subscriberList, isSubscriberListLoading] = useGetAllSubscribers(shouldReload);
  const [sortedItems, setSortedItems] = React.useState<SubscriberRecord[]>(subscriberList ?? []);
  const [filterByColumn, setFilterByColumn] = React.useState<string>('');
  const [filterByValue, setFilterByValue] = React.useState<string>('');

  const onColumnClickSort = (ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
    let isSortedDescending = column.isSortedDescending ?? false;

    // If we've sorted this column, flip it.
    if (column.isSorted) {
      isSortedDescending = !isSortedDescending;
    }

    setSortedItems(prevItems => {
      const itemsCopy = [...prevItems];
      sortItems(itemsCopy, column.key!, isSortedDescending);
      return itemsCopy;
    });
    
    var newSubscriberTableColumns = subscriberTableColumns.map(col => {
      col.isSorted = col.key === column.key;
      if (col.isSorted) {
        col.isSortedDescending = isSortedDescending;
      }
      return col;
    });
    
    setSubscriberTableColumns(newSubscriberTableColumns);
  };

  const [subscriberTableColumns, setSubscriberTableColumns] = React.useState<IColumn[]>([
    {
      key: 'editIcon',
      name: '',
      minWidth: 20,
      maxWidth: 50,
      isResizable: true,
      onColumnClick: onColumnClickSort
    },
    {
      key: 'name',
      name: 'Consumer Name',
      minWidth: 150,
      maxWidth: 200,
      isResizable: true,
      isSorted: true,
      onColumnClick: onColumnClickSort
    },
    {
      key: 'id',
      name: 'Consumer ID',
      minWidth: 300,
      maxWidth: 350,
      isResizable: true,
      onColumnClick: onColumnClickSort
    },
    {
      key: 'serviceTreeName',
      name: 'Service Tree Name',
      minWidth: 150,
      maxWidth: 200,
      isResizable: true,
      onColumnClick: onColumnClickSort
    },
    {
      key: 'serviceTreeID',
      name: 'Service Tree Id',
      minWidth: 300,
      maxWidth: 350,
      isResizable: true,
      onColumnClick: onColumnClickSort
    },
    {
      key: 'securityGroup',
      name: 'Security Group',
      minWidth: 100,
      maxWidth: 200,
      isResizable: true,
      onColumnClick: onColumnClickSort
    },
    {
      key: 'status',
      name: 'Status',
      minWidth: 150,
      maxWidth: 200,
      isResizable: true,
      onColumnClick: onColumnClickSort
    },
  ]);

  const commandBarItems: ICommandBarItemProps[] = [
    {
      key: 'addConsumer',
      text: 'Add Consumer',
      iconProps: { iconName: 'Add' },
      onClick: () => {
        setAddSubscriberPanelOpen(true);
      },
    },
    commandBarRefreshItem
  ];

  const onRenderItemColumn = (item: SubscriberRecord, index: number, column: IColumn): React.ReactNode => {
    if (column.key == 'name') {
      return (
        <div aria-label={`Column header: ${column.key}. Cell content: ${item.name}`}>
          <StyledLink
            title="Go to individual consumer"
            to={`/consumers/${item.id}`}     
            ariaLabel={`Go to consumer ${item.name}`}
            onClick={() => {}}
            usageEvent={{
              feature: 'HRDP',
              subFeature: 'HRDP.Consumers',
            }}
          >
            {item.name}
          </StyledLink>
        </div>
      );
    }
    else if (column.key == 'editIcon') {
      const isInRenewalPeriod = item.renewalEnabled && (
        item.prodSubscriptionExpirationStatus === 1 || item.prodSubscriptionExpirationStatus === 2 ||
        item.nonProdSubscriptionExpirationStatus === 1 || item.nonProdSubscriptionExpirationStatus === 2);
      const isDisabled = item.status === SubscriberStatusEnum.PendingApproval || isInRenewalPeriod;

      return (
        <div aria-label={`Column header: ${column.key}`}>
          <Styled.InlineTooltipWrapper>
            <TooltipHost content="Edit Consumer" id="editConsumerIcon">
              <IconButton iconProps={{ iconName: 'EditSolidMirrored12' }} aria-label="Edit Consumer" onClick={() => onHandleEditConsumer(item)} disabled={isDisabled}/>
            </TooltipHost>
          </Styled.InlineTooltipWrapper>
        </div>
      );
    } else if (column.key === 'status') {
      if (item.status === SubscriberStatusEnum.PendingApproval) {
        return <StatusStyled.SubmittedStatus>Pending Approval</StatusStyled.SubmittedStatus>;
      } else if (item.status === SubscriberStatusEnum.Approved) {
        return <StatusStyled.ApprovedStatus>Approved</StatusStyled.ApprovedStatus>;
      } else if (item.status === SubscriberStatusEnum.Rejected) {
        return <StatusStyled.RejectedStatus>Rejected</StatusStyled.RejectedStatus>;
      }
    }
    
    const value = item[column.key as keyof SubscriberRecord];
    if(! Array.isArray(value))
      return <Text>{value}</Text>;
    return '';
  };

  const onRenderRow = (props: IDetailsRowProps) => {
    return <DetailsRow aria-busy={true} {...props} />;
  };

  const onHandleEditConsumer = (item: SubscriberRecord) => {
    setSelectedEditSubscriber(item);
    setEditSubscriberPanelOpen(true);
  };

  const dismissAddSubscriberPanel = () => {
    setAddSubscriberPanelOpen(false);
  };

  const dismissEditSubscriberPanel = () => {
    setSelectedEditSubscriber(null);
    setEditSubscriberPanelOpen(false);
  };

  const closeConfirmationPopup = () => {
    setShowConfirmationPopup(false);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
  };

  const filterColumnDropdown: IDropdownOption[] = [{ key: '', text: ''}].concat(subscriberTableColumns.filter(col => col.key!='editIcon').map((col, index) => {
    return { key: col.key, text: col.name};
  }));
  
  const onHandleFilterColumn = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
    if (item) {
      setFilterByColumn(item.key.toString());
      onHandleFilterValue(null,filterByValue);
    }
  }
  const onHandleFilterValue = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string) => {
    setFilterByValue(newValue);
    setSortedItems(prevItems => {
      const itemsCopy = [...subscriberList];
      if(newValue == '')
        return itemsCopy;
      newValue = newValue.toLowerCase();
      //Filter by Column data
      if (filterByColumn === "name") {
        return itemsCopy.filter(i => i.name.toLowerCase().includes(newValue));
      }
      else if (filterByColumn === "id") {
        return itemsCopy.filter(i => i.id.toLowerCase().includes(newValue));
      }
      else if (filterByColumn === "serviceTreeName") {
        return itemsCopy.filter(i => i.serviceTreeName.toLowerCase().includes(newValue));
      }
      else if (filterByColumn === "serviceTreeID") {
        return itemsCopy.filter(i => i.serviceTreeID.toLowerCase().includes(newValue));
      }
      else if (filterByColumn === "securityGroup") {
        return itemsCopy.filter(i => i.securityGroup.toLowerCase().includes(newValue));
      }
      else if (filterByColumn === "status") {
        return itemsCopy.filter(i => i.status.toLowerCase().includes(newValue));
      }
      else if (filterByColumn === "") {
        // Global search across all relevant fields
        return itemsCopy.filter(i => i.name.toLowerCase().includes(newValue) ||
                                  i.id.toLowerCase().includes(newValue) ||
                                  i.serviceTreeName.toLowerCase().includes(newValue) ||
                                  i.serviceTreeID.toLowerCase().includes(newValue) ||
                                  i.securityGroup.toLowerCase().includes(newValue)||
                                  i.status.toLowerCase().includes(newValue)
                                );
      }
      return itemsCopy;
    });
  }

  // Update state after data load
  React.useEffect(() => {
    if(!isSubscriberListLoading){
      setSortedItems(sortItems(subscriberList, "name", false));
    }
  }, [isSubscriberListLoading]);

  return (
    <Styled.SubscriberPageContainer data-testid="subscriber-page">
      <title>Consumers</title>

      <Styled.SubscriberPageHeadingSection>
        <PortalPageTitleText>Consumers</PortalPageTitleText>
      </Styled.SubscriberPageHeadingSection>

      <Styled.SubscriberButtonsTableSection>
        <Styled.SubscriberTableScrollWrapper>
          <Styled.SubscriberTableContentWrapper>
            <StatusStyled.PortalManagementFilterDiv>
              <Styled.CompactPortalCommandBar items={commandBarItems} ariaLabel="Command Bar" />
              <FontIcon aria-label="filter" iconName="Filter">
              </FontIcon>
              <label>Filter by Column</label>
              <StatusStyled.DropdownStyled
                data-testid="select-column"
                placeholder="Filter by column"
                ariaLabel="Column"
                onChange={onHandleFilterColumn}
                selectedKey={filterByColumn}
                options={filterColumnDropdown}
                multiSelect={false}
              />
              <label>Filter by Value</label>
              <TextField
                onChange={onHandleFilterValue}
                placeholder="Enter text"
                value={filterByValue}
              />
            </StatusStyled.PortalManagementFilterDiv>

            <Styled.SubscriberTableViewport>
              <ShimmeredDetailsList
                items={sortedItems}
                enableShimmer={isSubscriberListLoading}
                columns={subscriberTableColumns}
                onRenderItemColumn={onRenderItemColumn}
                onRenderRow={onRenderRow}
                selectionMode={SelectionMode.none}
                layoutMode={DetailsListLayoutMode.fixedColumns}
                constrainMode={ConstrainMode.unconstrained}
                checkboxVisibility={CheckboxVisibility.hidden}
                ariaLabelForShimmer="Subscribers Table is being fetched"
                ariaLabelForGrid="Subscribers Table"
              />
            </Styled.SubscriberTableViewport>
          </Styled.SubscriberTableContentWrapper>
        </Styled.SubscriberTableScrollWrapper>
      </Styled.SubscriberButtonsTableSection>

      {addSubscriberPanelOpen && (
        <AddSubscriberPanel
          isOpen={addSubscriberPanelOpen}
          dismissPanel={dismissAddSubscriberPanel}
          setShowConfirmationPopup={setShowConfirmationPopup}
          setShowErrorMessage={setShowErrorMessage}
        />
      )}
      {editSubscriberPanelOpen && (
        <AddSubscriberPanel
          isOpen={editSubscriberPanelOpen}
          dismissPanel={dismissEditSubscriberPanel}
          setShowConfirmationPopup={setShowConfirmationPopup}
          setShowErrorMessage={setShowErrorMessage}
          isUpdateMode= {true}
          defaultSubscriber={selectedEditSubscriber}
        />
      )}

      <Dialog
        hidden={!showConfirmationPopup}
        onDismiss={closeConfirmationPopup}
        dialogContentProps={{
          type: DialogType.normal,
        }}
        modalProps={{
          isBlocking: showSuccessMessage || showErrorMessage ? false : true,
          styles: { main: { maxWidth: 450 } },
        }}
      >
        <Stack>
          <Styled.DialogTitleLabel>
            {!showSuccessMessage && !showErrorMessage && PopupText.submitRequestInProgress}
            {showSuccessMessage && PopupText.submitRequestSuccess}
            {showErrorMessage && PopupText.userError}
          </Styled.DialogTitleLabel>
          {!showSuccessMessage && !showErrorMessage && <ProgressIndicator></ProgressIndicator>}
          <Styled.DialogBodyText>
            {!showSuccessMessage && !showErrorMessage && ''}
            {showSuccessMessage && PopupText.requestSuccessDescription}
            {showErrorMessage && PopupText.userErrorDescription}
          </Styled.DialogBodyText>
        </Stack>
      </Dialog>
    </Styled.SubscriberPageContainer>
  );
}

const StyledLink = styled(Link)`
  font-size: 14px;
  line-height: 20px;
`;
